import { Chip, TableCell, TableRow } from '@mui/material';
import { FC, Fragment, Reducer, useCallback, useEffect, useReducer } from 'react';
// components
import { Alert } from 'components/common/Alert';
import TableLoader from 'components/common/TableLoader';
import NoDataFound from 'components/common/NoDataFound';
import TableContainer from 'components/common/TableContainer';
import TableComponent from 'components/common/TableComponent';
import TableSearchField from 'components/common/TableSearchField';
import ActionMenuDropdown from 'components/common/ActionMenuDropdown';
import TableTabsComponent from 'components/common/TableTabsComponent';
// constants, reducers, graphql, styles
import {
  HTTP_STATUS,
  LOADING_TABLE_ROWS,
  ACTIVE_TEXT,
  INACTIVE_TEXT,
  TabEnum,
  SWEETENER_COMPONENT_TABLE_HEADER,
  TABS,
  EDIT_SWEETENER_COMPONENT_ROUTE,
} from 'constants/index';
import { textWhiteSpace } from 'styles/commonComponentStyle';
import {
  useFindAllSweetenersComponentLazyQuery,
  SweetenerComponentsPayload,
  useUpdateSweetenerComponentMutation,
} from 'generated/graphql';
import {
  State,
  Action,
  ActionType,
  initialState,
  sweetenerComponentReducer,
} from 'reducer/sweetenerComponentReducer';

const SweetenerComponentTable: FC = () => {
  const [state, dispatch] = useReducer<Reducer<State, Action>>(sweetenerComponentReducer, initialState);
  const { page, rowsPerPage, search, count, data, activeTab } = state;

  const [findAllSweetenersComponents, { loading: fetchLoading, error }] =
    useFindAllSweetenersComponentLazyQuery({
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,

      onCompleted: (data) => {
        const { findAllSweetenersComponent } = data;
        const { response, data: sweetenersComponents, pagination } = findAllSweetenersComponent || {};
        const { status } = response || {};
        if (status === HTTP_STATUS.SUCCESS) {
          const { page, totalCount } = pagination || {};
          dispatch({ type: ActionType.SET_PAGE, page: page || 1 });
          dispatch({ type: ActionType.SET_COUNT, count: totalCount || 0 });
          dispatch({
            type: ActionType.SET_DATA,
            data: sweetenersComponents as SweetenerComponentsPayload['data'],
          });
        } else {
          resetPage();
        }
      },

      onError: () => {
        resetPage();
      },
    });

  const [updateSweetenerComponentStatus, { loading: updateLoading }] = useUpdateSweetenerComponentMutation({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { updateSweetenerComponent } = data;
      const { response } = updateSweetenerComponent || {};
      const { message, status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        Alert.success(message || '');
        fetchAllSweetenersComponents();
      } else {
        Alert.error(message ?? '');
      }
    },

    onError: ({ message }) => {
      Alert.error(message);
    },
  });

  const resetPage = () => {
    dispatch({ type: ActionType.SET_PAGE, page: 1 });
    dispatch({ type: ActionType.SET_COUNT, count: 0 });
    dispatch({ type: ActionType.SET_DATA, data: [] });
  };

  const fetchAllSweetenersComponents = useCallback(async () => {
    await findAllSweetenersComponents({
      variables: {
        findAllSweetenerComponentsInput: {
          paginationOptions: {
            limit: rowsPerPage,
            page,
          },
          search,
          ...(activeTab !== TabEnum.All && { isActive: activeTab === TabEnum.Active }),
        },
      },
    });
  }, [findAllSweetenersComponents, search, rowsPerPage, page, activeTab]);

  useEffect(() => {
    (!search.length || search.length > 2) && fetchAllSweetenersComponents();
  }, [fetchAllSweetenersComponents, search.length]);

  const tabHandler = (_: React.SyntheticEvent<Element, Event>, value: string) => {
    dispatch({ type: ActionType.SET_ACTIVE_TAB, activeTab: value as TabEnum });
  };

  const onActiveHandler = async (id: string, status: boolean) => {
    await updateSweetenerComponentStatus({
      variables: {
        updateSweetenerComponentInput: {
          id,
          isActive: !status,
        },
      },
    });
  };

  const loading = fetchLoading || updateLoading;

  const noData = Boolean((!fetchLoading && data?.length === 0) || error);

  return (
    <TableContainer>
      <TableTabsComponent activeTab={activeTab} tabsList={TABS} tabHandler={tabHandler} />

      <TableSearchField
        search={search}
        onClear={() => dispatch({ type: ActionType.SET_SEARCH, search: '' })}
        onChange={(val) => dispatch({ type: ActionType.SET_SEARCH, search: val })}
      />

      <TableComponent
        noData={noData}
        page={page - 1}
        count={count}
        rowsPerPage={rowsPerPage}
        tableHeader={SWEETENER_COMPONENT_TABLE_HEADER}
        setPage={(p: number) => dispatch({ type: ActionType.SET_PAGE, page: p + 1 })}
        setRowsPerPage={(r: number) => dispatch({ type: ActionType.SET_ROWS_PER_PAGE, rowsPerPage: r })}>
        {loading ? (
          <TableLoader columns={4} rows={LOADING_TABLE_ROWS} />
        ) : (
          <Fragment>
            {data?.map((cell) => {
              const { id, name, costPerKg, isActive } = cell || {};
              return (
                <TableRow key={id}>
                  <TableCell sx={textWhiteSpace}>{name ?? '--'}</TableCell>
                  <TableCell>{costPerKg ? `$${costPerKg}` : '--'}</TableCell>

                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={isActive ? ACTIVE_TEXT : INACTIVE_TEXT}
                      color={isActive ? 'success' : 'error'}
                    />
                  </TableCell>
                  <TableCell>
                    <ActionMenuDropdown
                      id={id ?? ''}
                      isActive={!!isActive}
                      editRoute={EDIT_SWEETENER_COMPONENT_ROUTE}
                      onActiveHandler={onActiveHandler}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </Fragment>
        )}
      </TableComponent>

      <NoDataFound noData={noData} />
    </TableContainer>
  );
};

export default SweetenerComponentTable;
